import { SUBJECTS, type SchoolLevel, type SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import {
  LEVEL_LABEL,
  type DemoChoice,
  type DemoDifficulty,
  type DemoExercise,
  type DemoStep,
} from './types';

/**
 * Loader de l'aperçu anonyme d'un exercice précis (route `/demo/exercise/$id`).
 *
 * S'appuie sur la RPC `get_exercise_demo` (anon-callable, security definer)
 * qui ne renvoie que les 2 premières étapes d'un exercice publié.
 */

/** Nombre d'étapes jouables dans l'aperçu anonyme — aligné sur FREE_STEPS_COUNT. */
export const DEMO_EXERCISE_STEP_LIMIT = 2;

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: unknown }>;

function shuffled<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

function normalizeChoice(raw: unknown): DemoChoice | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.id !== 'string' ||
    typeof r.label_md !== 'string' ||
    typeof r.is_correct !== 'boolean'
  ) {
    return null;
  }
  const choice: DemoChoice = { id: r.id, label_md: r.label_md, is_correct: r.is_correct };
  if (typeof r.feedback_md === 'string') choice.feedback_md = r.feedback_md;
  return choice;
}

function normalizeStep(raw: unknown): DemoStep | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.id !== 'string' ||
    typeof r.prompt_md !== 'string' ||
    typeof r.explanation_md !== 'string'
  ) {
    return null;
  }
  if (!Array.isArray(r.choices)) return null;
  const choices = r.choices.map(normalizeChoice).filter((c): c is DemoChoice => c !== null);
  // Une étape jouable a au moins 2 choix dont une bonne réponse.
  if (choices.length < 2 || !choices.some((c) => c.is_correct)) return null;
  return {
    id: r.id,
    prompt_md: r.prompt_md,
    explanation_md: r.explanation_md,
    choices: shuffled(choices),
  };
}

/** Difficulté valide ⇒ telle quelle, sinon `null` (l'habillage est optionnel). */
function normalizeDifficulty(raw: unknown): DemoDifficulty | null {
  return raw === 'easy' || raw === 'medium' || raw === 'hard' ? raw : null;
}

/** Slug de matière connu ⇒ tel quel, sinon `null`. */
function normalizeSubject(raw: unknown): SubjectSlug | null {
  return SUBJECTS.some((s) => s.value === raw) ? (raw as SubjectSlug) : null;
}

/** Niveau scolaire connu ⇒ tel quel, sinon `null`. */
function normalizeLevel(raw: unknown): SchoolLevel | null {
  return typeof raw === 'string' && raw in LEVEL_LABEL ? (raw as SchoolLevel) : null;
}

/** Chaîne non vide ⇒ telle quelle, sinon `null` (champ d'habillage absent). */
function normalizeText(raw: unknown): string | null {
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

/**
 * Convertit le payload brut de la RPC `get_exercise_demo` en `DemoExercise`.
 * Pur (testable) : aucune dépendance Supabase. Renvoie `null` si le payload
 * est inexploitable (exercice absent, aucune étape valide, titre manquant).
 *
 * Les champs d'habillage (`cover_url`, `subject`, `level`, `context_md`,
 * `statement_md`, `difficulty`, `estimated_minutes`) sont optionnels : ils
 * permettent à l'aperçu d'avoir le même design qu'un exercice réel, mais leur
 * absence n'invalide jamais le payload.
 */
export function mapExerciseDemoPayload(raw: unknown): DemoExercise | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.title !== 'string' || !Array.isArray(r.steps)) return null;
  const steps = r.steps
    .map(normalizeStep)
    .filter((s): s is DemoStep => s !== null)
    .slice(0, DEMO_EXERCISE_STEP_LIMIT);
  if (steps.length === 0) return null;
  return {
    title: r.title,
    chapter_label: typeof r.chapter_label === 'string' ? r.chapter_label : '',
    steps,
    cover_url: normalizeText(r.cover_url),
    subject: normalizeSubject(r.subject),
    level: normalizeLevel(r.level),
    context_md: normalizeText(r.context_md),
    statement_md: normalizeText(r.statement_md),
    difficulty: normalizeDifficulty(r.difficulty),
    estimated_minutes: typeof r.estimated_minutes === 'number' ? r.estimated_minutes : null,
  };
}

/**
 * Charge l'aperçu anonyme d'un exercice (2 premières étapes guidées).
 * Lève une erreur si l'exercice est introuvable, non publié, ou inexploitable.
 */
export async function loadExerciseDemo(exerciseId: string): Promise<DemoExercise> {
  const { data, error } = await (supabase.rpc as unknown as UntypedRpc)('get_exercise_demo', {
    p_exercise_id: exerciseId,
  });
  if (error) throw error;
  const exercise = mapExerciseDemoPayload(data);
  if (!exercise) throw new Error('Exercice introuvable ou indisponible.');
  return exercise;
}
